import React, { useState, useEffect } from 'react';
import { FaUsers, FaUserCheck, FaCrown, FaExclamationCircle, FaArrowUp, FaArrowDown, FaSpinner, FaMars, FaVenus } from 'react-icons/fa';
import api from '../../services/api';
// We need to install recharts if not available, but user said "develop frontend", assuming we can use standard libs or mock visualisation. 
// For now, let's keep it simple with pure CSS bars for gender if recharts is missing, or ask to install. 
// User environment usually has standard react stuff. I'll use simple visualisations to be safe or just placeholders if I can't confirm.
// Actually, let's make a beautiful Gender Card.

const StatCard = ({ title, value, change, icon: Icon, color }) => (
    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
        <div className="flex justify-between items-start">
            <div>
                <p className="text-sm font-medium text-gray-500 mb-1">{title}</p>
                <h3 className="text-3xl font-bold text-gray-800">{value}</h3>
            </div>
            <div className={`p-3 rounded-xl bg-${color}-50 text-${color}-600`}>
                <Icon className="text-xl" />
            </div>
        </div>
       
    </div>
);

const AdminDashboard = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            const res = await api.get('/admin/stats');
            setStats(res.data);
        } catch (error) {
            console.error('Failed to fetch admin stats:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div className="flex justify-center p-12"><FaSpinner className="animate-spin text-4xl text-rose-600" /></div>;
    if (!stats) return <div className="text-center p-12 text-gray-500">Failed to load dashboard data.</div>;

    const totalGender = (stats.genderRatio?.male || 0) + (stats.genderRatio?.female || 0) || 1;
    const malePercent = Math.round(((stats.genderRatio?.male || 0) / totalGender) * 100);
    const femalePercent = Math.round(((stats.genderRatio?.female || 0) / totalGender) * 100);

    return (
        <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                    title="Total Users"
                    value={stats.totalUsers}
                    change={12}
                    icon={FaUsers}
                    color="blue"
                />
                <StatCard
                    title="Total Profiles"
                    value={stats.totalProfiles}
                    change={5}
                    icon={FaUserCheck}
                    color="green"
                />
                <StatCard
                    title="Subscription Members"
                    value={stats.premiumUsers}
                    change={8}
                    icon={FaCrown}
                    color="yellow"
                />
                <StatCard
                    title="Pending Reports"
                    value={stats.pendingReports}
                    change={-2}
                    icon={FaExclamationCircle}
                    color="red"
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Gender Ratio */}
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                    <h3 className="text-lg font-bold text-gray-800 mb-6">User Demographics</h3>
                    <div className="flex items-center justify-between mb-8">
                        <div className="text-center">
                            <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 mb-2 mx-auto">
                                <FaMars className="text-3xl" />
                            </div>
                            <p className="font-bold text-gray-800">{stats.genderRatio?.male || 0}</p>
                            <p className="text-sm text-gray-500">Male</p>
                        </div>
                        <div className="text-center flex-1 px-4">
                            <div className="w-full bg-gray-100 rounded-full h-2 mb-2 overflow-hidden">
                                <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${malePercent}%` }}></div>
                            </div>
                            <p className="text-xs text-gray-400 font-bold">{malePercent}% vs {femalePercent}%</p>
                            <div className="w-full bg-gray-100 rounded-full h-2 mt-2 overflow-hidden">
                                <div className="bg-pink-500 h-2 rounded-full" style={{ width: `${femalePercent}%` }}></div>
                            </div>
                        </div>
                        <div className="text-center">
                            <div className="w-16 h-16 rounded-full bg-pink-100 flex items-center justify-center text-pink-600 mb-2 mx-auto">
                                <FaVenus className="text-3xl" />
                            </div>
                            <p className="font-bold text-gray-800">{stats.genderRatio?.female || 0}</p>
                            <p className="text-sm text-gray-500">Female</p>
                        </div>
                    </div>
                </div>

                {/* Recent Registrations */}
<div className="lg:col-span-2 bg-white p-4 rounded-xl shadow border border-gray-100">
    <div className="flex items-center justify-between mb-4">
        <div>
            <h3 className="text-lg font-semibold text-gray-800">
                Recent Registrations
            </h3>
            <p className="text-xs text-gray-500">
                Recently joined users
            </p>
        </div>
    </div>

    <div className="space-y-2">
        {stats.recentUsers?.map((user) => (
            <div
                key={user._id}
                className="flex items-center justify-between px-3 py-2 rounded-lg border border-gray-100 hover:bg-gray-50 transition"
            >
                <div className="flex items-center space-x-3">
                    <div className="w-9 h-9 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-semibold">
                        {user.name?.[0]?.toUpperCase() || 'U'}
                    </div>

                    <div>
                        <p className="text-sm font-medium text-gray-800 leading-tight">
                            {user.name}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span className="capitalize">{user.role}</span>
                            <span>•</span>
                            <span className="truncate max-w-[160px]">
                                {user.email}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="text-right text-xs text-gray-500">
                    <p className="font-medium text-gray-700">
                        {new Date(user.createdAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                        })}
                    </p>
                    <p>
                        {new Date(user.createdAt).toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                        })}
                    </p>
                </div>
            </div>
        ))}
    </div>
</div>

            </div>
        </div>
    );
};

export default AdminDashboard;
